import {useMultiRef} from '@github-ui/use-multi-ref'

/** Handle exposed by list items so a parent can move focus to them. Returns whether the item took focus. */
export interface FocusableItemHandle {
  focus: () => boolean
}

/** Focuses `element` if it can currently receive focus. Returns whether it did. */
export function focusElement(element: HTMLElement | null | undefined) {
  // checkVisibility is missing in Safari < 17.4 and Firefox < 106
  if (!element?.isConnected || !(element.checkVisibility?.() ?? true) || element.matches(':disabled')) return false
  element.focus()
  return element.ownerDocument.activeElement === element
}

/**
 * Tracks a ref per list item (keyed by index) so that, when an item is deleted, focus can move to the closest
 * earlier item that can take it, or to `fallback` when there is none.
 */
export function useFocusPreviousItem(fallback: () => void) {
  const itemRefs = useMultiRef<number, FocusableItemHandle>()

  function focusPrevious(index: number) {
    for (let previous = index - 1; previous >= 0; previous--) {
      // The loop exits at the first item that takes focus
      // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-in-loop
      if (itemRefs(previous).current?.focus()) return
    }
    fallback()
  }

  return {itemRefs, focusPrevious}
}
